import { Collapse } from 'antd';
import type {
  EpiComposition,
  EpiDocumentType,
  EpiSectionNode,
} from '../types/types';
import CompositionPanel from './CompositionPanel';
import styled from 'styled-components';
import EpiMenuHeader from './EpiMenuHeader';
import { useEpiContext } from '../context/EpiContext';
import { useState } from 'react';

const { Panel } = Collapse;

interface IProps {
  onClick: (node: EpiSectionNode, compId: string) => void;
  docType: EpiDocumentType;
  compositions: EpiComposition[];
}

const EpiMenu: React.FC<IProps> = ({ onClick, docType, compositions }) => {
  const { setSelectedComposition } = useEpiContext();
  const [renamingCompositionId, setRenamingCompositionId] = useState<
    string | null
  >(null);
  return (
    <>
      <CollapseWrapper>
        <Collapse
          accordion
          expandIconPosition="end"
          bordered={false}
          style={{ background: 'transparent' }}
          defaultActiveKey={0}
        >
          {compositions?.map((composition, compIdx) => (
            <Panel
              collapsible={
                renamingCompositionId === composition.id
                  ? 'disabled'
                  : undefined
              }
              key={compIdx.toString()}
              header={
                <EpiMenuHeader
                  composition={composition}
                  docType={docType}
                  renameOpen={renamingCompositionId === composition.id}
                  showRename={() => setRenamingCompositionId(composition.id)}
                  hideRename={() => setRenamingCompositionId(null)}
                />
              }
            >
              <CompositionPanel
                key={composition.id}
                compIdx={compIdx}
                compositionId={composition.id}
                docType={docType}
                onClick={node => {
                  onClick(node, composition.id);
                  setSelectedComposition(composition.id);
                }}
              />
            </Panel>
          ))}
        </Collapse>
      </CollapseWrapper>
    </>
  );
};

const CollapseWrapper = styled.div`
  /* Each composition is one card: dark header, section list beneath it. The
     radii meet so the two read as a single surface rather than two stacked
     boxes — the list supplies the bottom corners, see menuSurfaceStyles. */
  .ant-collapse-item {
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  }

  .ant-collapse-header {
    position: relative;
    /* Chosen against the ePI's own blues rather than taken from the palette:
       the neutral gray.800 that stood here read cold beside them. If it ever
       earns a token, this is the one place that uses it. */
    background: #202947;
    border-radius: 12px 12px 0 0 !important;
    color: #ffffff !important;
    padding: 10px !important;
    padding-left: 16px !important;
  }

  /* Collapsed, the header is the whole card. */
  .ant-collapse-item:not(.ant-collapse-item-active) .ant-collapse-header {
    border-radius: 12px !important;
  }

  /* The section list carries its own padding, and the panel body is part of
     the card — antd leaves it transparent, which showed the page through. */
  .ant-collapse-content {
    background: ${({ theme }) => theme?.colors?.surface || '#ffffff'};
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .anticon {
    color: #ffffff !important;
  }

  /* Clear of the row's ... menu, which sits at the right edge. */
  .ant-collapse-expand-icon {
    position: absolute;
    right: 46px;
  }
`;

export default EpiMenu;
